// src/pricing/tiers.js
import { uid } from "./id";

const ensureArray = (v) => (Array.isArray(v) ? v : []);
const isNil = (v) => v === null || v === undefined;

const toNumber = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};

const cloneRange = (r) => ({
    _id: r?._id ?? uid(),
    start: toNumber(r?.start, 1),
    end: isNil(r?.end) ? null : toNumber(r.end),
    // cents
    low: toNumber(r?.low, 0),
    medium: toNumber(r?.medium, 0),
    high: toNumber(r?.high, 0),
});

function samePrice(a, b, seasonal) {
    if (!a || !b) return false;
    if (seasonal) {
        return (a.low ?? 0) === (b.low ?? 0)
            && (a.medium ?? 0) === (b.medium ?? 0)
            && (a.high ?? 0) === (b.high ?? 0);
    }
    // FIXED：只用 low（medium/high 会被强制等于 low）
    return (a.low ?? 0) === (b.low ?? 0);
}

/**
 * 自动继承 0 价格：
 * - day>1 的 tier，如果某字段为 0，则继承前一个 tier 同字段的值
 * - 只对 0 生效，非 0 不动（满足“只自动填充 0”）
 */
function inheritZeros(ranges, seasonal) {
    if (!ranges.length) return ranges;

    for (let i = 1; i < ranges.length; i++) {
        const prev = ranges[i - 1];
        const cur = ranges[i];

        // low=0 -> 继承
        if ((cur.low ?? 0) === 0) cur.low = prev.low ?? 0;

        if (seasonal) {
            if ((cur.medium ?? 0) === 0) cur.medium = prev.medium ?? (prev.low ?? 0);
            if ((cur.high ?? 0) === 0) cur.high = prev.high ?? (prev.low ?? 0);
        } else {
            // FIXED：medium/high 始终跟 low
            cur.medium = cur.low ?? 0;
            cur.high = cur.low ?? 0;
        }
    }

    // tier[0] 如果 seasonal 缺 medium/high，回退 low；FIXED 强制等于 low
    const first = ranges[0];
    if (seasonal) {
        if (isNil(first.medium)) first.medium = first.low ?? 0;
        if (isNil(first.high)) first.high = first.low ?? 0;
    } else {
        first.medium = first.low ?? 0;
        first.high = first.low ?? 0;
    }

    return ranges;
}

/**
 * 合并相邻且同价的 tiers：
 * - 保留前一个 _id
 * - end 延伸到后一个 end
 */
function mergeAdjacentSamePrice(ranges, seasonal) {
    if (ranges.length <= 1) return ranges;

    const out = [ranges[0]];
    for (let i = 1; i < ranges.length; i++) {
        const prev = out[out.length - 1];
        const cur = ranges[i];

        if (samePrice(prev, cur, seasonal)) {
            // 合并：prev.end = cur.end
            prev.end = cur.end;
        } else {
            out.push(cur);
        }
    }
    return out;
}

/**
 * normalizeRanges（升级版）：
 * - start 从 1
 * - 连续贴合
 * - 最后一条 end=null
 * - seasonal=true: 保持 low/medium/high
 * - seasonal=false(FIXED): 只认 low，medium/high 强制=low
 * - 0 价格自动继承前一 tier（只对 0）
 * - 自动合并相邻同价 tier
 */

export function normalizeRangesEdit(inputRanges, seasonal) {
    const ranges = ensureArray(inputRanges).map(cloneRange);

    if (ranges.length === 0) {
        return [{ _id: uid(), start: 1, end: null, low: 0, medium: 0, high: 0 }];
    }

    ranges.sort((a, b) => a.start - b.start);
    ranges[0].start = 1;

    // 连续贴合 + 剪掉 last.end==null 之后的多余条目
    for (let i = 1; i < ranges.length; i++) {
        const prev = ranges[i - 1];
        const cur = ranges[i];

        if (prev.end == null) {
            ranges.splice(i);
            break;
        }

        cur.start = Number(prev.end) + 1;
        if (cur.end != null && cur.end < cur.start) cur.end = cur.start;
    }

    ranges[ranges.length - 1].end = null;

    // FIXED：先强制 medium/high=low（避免脏数据）
    if (!seasonal) {
        for (const r of ranges) {
            r.medium = r.low ?? 0;
            r.high = r.low ?? 0;
        }
    } else {
        // SEASON：medium/high 缺省回退 low（但不覆盖非 0）
        for (const r of ranges) {
            const base = r.low ?? 0;
            if (isNil(r.medium)) r.medium = base;
            if (isNil(r.high)) r.high = base;
        }
    }

    // 0 自动继承（只对 0）
    inheritZeros(ranges, seasonal);

    // ⚠️ 不做合并：编辑态保留用户意图
    ranges[0].start = 1;
    ranges[ranges.length - 1].end = null;

    return ranges;
}

export function normalizeRangesSave(inputRanges, seasonal) {
    const ranges = normalizeRangesEdit(inputRanges, seasonal);

    // 保存态才合并同价
    const merged = mergeAdjacentSamePrice(ranges, seasonal);

    merged[0].start = 1;
    merged[merged.length - 1].end = null;

    return merged;
}

/**
 * splitLastInfiniteTier：最后一条 end=null 拆成两条
 */
export function splitLastInfiniteTier(inputRanges, cutDay, seasonal) {
    const ranges = normalizeRangesEdit(inputRanges, seasonal);
    console.log("seasonal", seasonal);
    const last = ranges[ranges.length - 1];

    if (last.end !== null) throw new Error("Last tier must be infinite (end=null).");

    const cut = toNumber(cutDay, NaN);
    if (!Number.isFinite(cut) || cut <= 0) throw new Error("cutDay must be a positive number.");
    if (cut < last.start) throw new Error(`cutDay must be >= ${last.start}.`);

    const left = { ...last, _id: uid(), end: cut };
    const right = { ...last, _id: uid(), start: cut + 1, end: null };

    return normalizeRangesEdit([...ranges.slice(0, -1), left, right], seasonal);
}

/**
 * updateTierEnd：编辑 end（最后一行不允许）
 */
export function updateTierEnd(inputRanges, idx, nextEnd, seasonal) {
    const ranges = normalizeRangesEdit(inputRanges, seasonal);
    if (idx >= ranges.length - 1) return ranges;

    const cur = ranges[idx];
    const end = toNumber(nextEnd, NaN);
    if (!Number.isFinite(end)) return ranges;

    const safeEnd = Math.max(cur.start, end);
    const next = ranges.map((r, i) => (i === idx ? { ...r, end: safeEnd } : r));
    return normalizeRangesEdit(next, seasonal);
}

// 在某天 day 把 tier 分裂成两段： [start..day-1] + [day..end]
export function splitTierAtDay(tier, day) {
    const left = { ...tier, _id: uid(), end: day - 1 };
    const right = { ...tier, _id: uid(), start: day };
    return [left, right];
}

export function formatTierLabel(start, end) {
    if (end == null) return `Days ${start}+`;
    if (start === end) return `Day ${start}`;
    return `Days ${start}–${end}`;
}

// 把某天 day 从一个 tier 里“单独隔离出来”
// 返回 1~3 段：left / mid / right（不存在的段不会返回）
export function splitTierToIsolateDay(tier, day) {
    const start = Number(tier.start ?? 1);
    const end = tier.end == null ? null : Number(tier.end);

    // end=null 表示无限大，这里用 Infinity 计算
    const endVal = end == null ? Infinity : end;

    if (day < start || day > endVal) return [{ ...tier }];

    // 已经是单天 tier
    if (end != null && start === end && day === start) return [{ ...tier }];

    const out = [];

    // left: start..day-1
    if (day > start) {
        out.push({ ...tier, _id: uid(), start, end: day - 1 });
    }

    // mid: day..day (单天)
    out.push({ ...tier, _id: uid(), start: day, end: day });

    // right: day+1..end
    if (day < endVal) {
        out.push({ ...tier, _id: uid(), start: day + 1, end: end }); // end 可以是 null
    }

    return out;
}